import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useGetAllContractsForAdminQuery } from '@/apis/extended/contractApi';
import {
  Message,
  useGetMessagesForAdminQuery,
  useSendMessageMutation,
} from '@/apis/extended/messageApi';
import { getContractPropertyAddress } from '@/utils/contractPropertyAddress';

const roleOptions = [
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'agent', label: 'Agent' },
  { value: 'seller_agent', label: 'Seller Agent' },
];

/** Thread title: property address (all stored shapes), else name / short ref. */
const threadTitle = (contract: any): string =>
  getContractPropertyAddress(contract).addressLine ||
  contract?.product?.name ||
  contract?.buyerInfo?.name ||
  (contract?._id ? `Contract …${String(contract._id).slice(-6)}` : '');

export const AdminMessenger = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContractId, setSelectedContractId] = useState('');
  const [toRole, setToRole] = useState('buyer');
  const [subject, setSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');

  const { data: contracts } = useGetAllContractsForAdminQuery();
  const { data: messagesData, isLoading } = useGetMessagesForAdminQuery(
    selectedContractId,
    { skip: !selectedContractId }
  );
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();

  const filteredContracts = useMemo(() => {
    if (!contracts) return [];
    return contracts.filter((contract: any) => {
      const address = threadTitle(contract);
      const buyerName = contract.buyer?.name || contract.buyerInfo?.name || '';
      const sellerName = contract.user?.name || '';
      return (
        address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        buyerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sellerName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [contracts, searchTerm]);

  const selectedContract = useMemo(() => {
    return filteredContracts.find(
      (contract: any) => contract._id === selectedContractId
    );
  }, [filteredContracts, selectedContractId]);

  const messages = useMemo(() => {
    const list = messagesData?.messages || [];
    return [...list].reverse();
  }, [messagesData]);

  const panelSx = {
    p: 2,
    bgcolor: 'background.paper',
    border: `1px solid ${theme.palette.greyShades.border1}`,
    borderRadius: 2,
    boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
  };

  const outlinedFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.primary,
      bgcolor: theme.palette.background.paper,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.greyShades.border1,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.text.primary, 0.22),
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiInputLabel-root': { color: 'text.secondary' },
  };

  const handleSendMessage = async () => {
    if (!selectedContractId || !messageBody.trim()) return;
    await sendMessage({
      contractId: selectedContractId,
      toRole: toRole as any,
      message: messageBody.trim(),
      subject: subject.trim() || undefined,
    });
    setMessageBody('');
    setSubject('');
  };

  return (
    <Box>
      <Typography
        variant="h3"
        sx={{ color: 'text.primary', fontWeight: 700, mb: 3 }}
      >
        Messenger
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
          gap: 3,
        }}
      >
        <Paper elevation={0} sx={panelSx}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2, ...outlinedFieldSx }}
          />
          <List dense sx={{ py: 0 }}>
            {filteredContracts.map((contract: any) => (
              <ListItemButton
                key={contract._id}
                selected={selectedContractId === contract._id}
                onClick={() => setSelectedContractId(contract._id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.25,
                  '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.16),
                    },
                  },
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                  },
                }}
              >
                <ListItemText
                  primary={threadTitle(contract) || 'Address N/A'}
                  secondary={`Contract ${contract.contractId || contract._id}`}
                  primaryTypographyProps={{
                    sx: {
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    },
                  }}
                  secondaryTypographyProps={{
                    sx: {
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      fontFamily: 'ui-monospace, monospace',
                    },
                  }}
                />
              </ListItemButton>
            ))}
            {!filteredContracts.length && (
              <Alert severity="info">No contracts found.</Alert>
            )}
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            p: 3,
            minHeight: '600px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!selectedContractId ? (
            <Alert severity="info">Select a contract to view messages.</Alert>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{ color: 'text.primary', fontWeight: 600 }}
                >
                  {threadTitle(selectedContract) || 'Contract Messages'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'ui-monospace, monospace',
                    mt: 0.5,
                  }}
                >
                  {selectedContract?.contractId || selectedContractId}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2, borderColor: 'divider' }} />

              <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
                {isLoading ? (
                  <Alert severity="info">Loading messages...</Alert>
                ) : (
                  messages.map((msg: Message) => (
                    <Box key={msg._id} sx={{ mb: 2 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.primary', fontWeight: 600 }}
                        >
                          {msg.fromUserId?.name ||
                            msg.fromUserId?.email ||
                            'Unknown'}
                        </Typography>
                        <Chip
                          label={msg.fromUserId?.role || 'user'}
                          size="small"
                          variant="outlined"
                          sx={{
                            color: 'text.secondary',
                            borderColor: theme.palette.greyShades.border1,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary' }}
                        >
                          {new Date(msg.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                      {msg.subject && (
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'text.primary', fontWeight: 600 }}
                        >
                          {msg.subject}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        {msg.message}
                      </Typography>
                    </Box>
                  ))
                )}
                {!messages.length && !isLoading && (
                  <Alert severity="info">
                    No messages for this contract yet.
                  </Alert>
                )}
              </Box>

              <Divider sx={{ mb: 2, borderColor: 'divider' }} />

              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  select
                  size="small"
                  label="Send To"
                  value={toRole}
                  onChange={(e) => setToRole(e.target.value)}
                  sx={outlinedFieldSx}
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Subject (optional)"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  sx={outlinedFieldSx}
                />
                <TextField
                  size="small"
                  label="Message"
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  multiline
                  minRows={3}
                  sx={outlinedFieldSx}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={isSending || !messageBody.trim()}
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    alignSelf: 'flex-start',
                  }}
                >
                  Send Message
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
